import { useEffect, useRef, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

const COMPACT_WIDTH_PX = 1100;
const LAYOUT_SELECTOR =
  ".content-wrapper, .main-panel, .sing-content-preserve";

/** True when viewport is below MUI `lg` (1200px) — covers resize and zoom. */
export function useCompactViewport() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("lg"), { noSsr: true });
}

function availableWidth(el: HTMLElement): number {
  const layout = el.closest(LAYOUT_SELECTOR) as HTMLElement | null;
  const layoutWidth = layout?.clientWidth ?? Number.POSITIVE_INFINITY;
  const selfWidth = el.clientWidth || el.getBoundingClientRect().width;
  return Math.min(selfWidth || layoutWidth, layoutWidth);
}

/**
 * Compact when viewport is narrow OR the measured container is under
 * COMPACT_WIDTH_PX (handles sidebar eating content width).
 * Uses the layout shell width so a wide table cannot block card mode.
 */
export function useCompactLayout(forceCompact = false) {
  const isCompactViewport = useCompactViewport();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerNarrow, setContainerNarrow] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const layout = el.closest(LAYOUT_SELECTOR) as HTMLElement | null;

    const measure = () => {
      const width = availableWidth(el);
      setContainerNarrow(width > 0 && width < COMPACT_WIDTH_PX);
    };

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (layout) ro.observe(layout);
    measure();
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return {
    containerRef,
    isCompact: forceCompact || isCompactViewport || containerNarrow,
  };
}
